import { Editor } from "tinymce";
import { axiosInstance } from "api/axios";
import EmailService from "api/emailService";
import parcelService from "api/parcelService";
import ebiOfficeService from "api/ebiOfficeService";
import { getCompanyPublicKeyBase64 } from "utils/encryptForCompany";
import { IEbiKeyBindingFields, signEbiKeyBinding } from "utils/ebiKeyBinding";
import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import ScanDocumentModal from "components/ScanDocumentModal";
import PuhOptionsPanel from "components/PuhOptionsPanel";
import NewSearchEntitiesWithCase, {
  fillIcon,
} from "components/NewSearchEntitiesWithCase";
import SearchCasesNew from "components/SearchCasesNew";
import KeysContext from "contexts/KeysContext";
import ModalsManagerContext, {
  CreateOutgoingParcelModalOptions,
} from "contexts/ModalsManager";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { IEntity } from "dto/IEntity";
import { IPackage } from "dto/IPackage";
import { IParcelTypes } from "dto/IParcelTypes";
import {
  IHybridSettings,
  defaultHybridSettings,
  EGovProcedingType,
  EGovCategoryService,
  EGovPrintingMode,
  EGovOverprintMode,
  EGovReturnService,
  EGovShippingMode,
} from "dto/IHybridSettings";
import useFiles, { FileExt } from "hooks/useFiles";
import * as openpgp from "openpgp";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useAuth } from "react-oidc-context";
import { decryptFile } from "utils/decryptFile";
import encryptContent from "utils/encryptString";
import { generateSingleBinaryFromMultiplePublicKeys } from "utils/generateSingleBinaryFromMultiplePublicKeys";
import { getBlob } from "utils/getBlob";
import { getErrorMessage } from "utils/getErrorMessage";
import { IError } from "dto/IError";
import { getFile } from "utils/getFile";
import { getKeys } from "utils/getKeys";
import CaseIcon from "assets/icons/case_sidebar.svg?react";
import settingService from "api/settingsService";
import {
  convertParcelTypeToKind,
} from "utils/getTypeOfParcel";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";
import { readAsBase64 } from "utils/readAsBase64";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import { uploadBlob } from "utils/uploadBlob";
import { handleCreate } from "utils/uploadFile";
import { CaseDossierWrapper } from "./CaseDossierWrapper";
import styles from "./styles.module.scss";
import { FC, IUploadedFile, Props } from "./typings";

import CreateCaseModal from "components/CreateCaseModal";
import Input from "components/Input";
import { IAttachment } from "dto/IAttachment";
import { ItemKind } from "dto/IKindItems";
import { useOnClickOutside } from "hooks/useClickOutside";
import { decryptPackage } from "utils/decryptPackage";
import { addToUrl, hasImages } from "utils/parseHTML";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import { IKey } from "dto/IKey";
import RichEditor from "components/RichEditor";
import classNames from "classnames";
import Chip from "components/Chip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import usePDF from "hooks/usePDF";
import ShowPreviewModal from "components/ShowPreviewModal";
import Spinner from "components/Spinner";
import moment from "moment";
import DossierAnimation from "components/DossierAnimation";
import { EEntityType } from "dto/EEntityType";
import { EParcelType } from "dto/EParcelType";
import { isAllowedEDeliveryAttachment } from "utils/isAllowedEDeliveryAttachment";
import { isAttachmentNameValid } from "utils/isAttachmentNameValid";
import { logger } from "utils/logger";
import encryptPrivateKey from "utils/encryptKey";
import { isNumber } from "utils/isNumber";
import { AbilityContext, Can } from "contexts/AbilityContext";
import { IOutgoingAccount } from "dto/IInitOutgoingParcelResponse";
import EmailIcon from "assets/icons/aemail.svg?react";
import NotaIcon from "assets/icons/nota_icon.svg?react";
import CourtIcon from "assets/icons/court.svg?react";
import Popper from "components/Popper";
import CreateOutgoingParcelFile from "components/CreateOutgoingParcelFile";
import pluginService from "api/pluginService";
import { blobToBase64 } from "utils/blobToBase64";
import { ICertificate } from "dto/ICertificate";
import HollowButton from "components/HollowButton";
import { IErrorMessage } from "components/ChatViewNewMessage/typings";
import AutoComplete from "components/AutoComplete";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

const CreateOutgoingParcelModal: FC<Props> = () => {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [isFullscreen] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);

  const [isHidden, setIsHidden] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const outSideRef = useRef(null);
  const outSideSelectEmailRef = useRef(null);
  const editorRef = useRef<any>(null);
  const [c, setCase] = useState<ICaseSimple | null>(null);
  const { data: footerData } =
    settingService.useGetFooter();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [initialEntities, setInitialEntities] = useState<IEntity[]>([]);
  const [entities, setEntities] = useState<IEntity[]>([]);
  const [cc, setCC] = useState<IEntity[]>([]);
  const [initialCc, setInitialCC] = useState<IEntity[]>([]);
  const [sourcePack, setSourcePack] = useState<IPackage>(null);

  const [errorMesssage, setErrorMessage] = useState<IErrorMessage>(null);

  const [fileToSign, setFileToSign] = useState<IUploadedFile | FileExt>(null);
  const [showCertificates, setShowCertificates] = useState<boolean>(false);
  const { mutate: signDocument, isLoading: isSignLoading } =
    pluginService.useSignDocument();

  const { data: certsData } = pluginService.useGetCerificates();
  const certs = useMemo(() => certsData?.data ?? [], [certsData]);

  const hasAnyExternalEDelvieryReceiver =
    entities.find(
      (e) =>
        e.parcelTypeSelected === IParcelTypes.ExternalEDelivery ||
        e.parcelTypeSelected === IParcelTypes.Puh,
    ) ||
    cc.find(
      (e) =>
        e.parcelTypeSelected === IParcelTypes.ExternalEDelivery ||
        e.parcelTypeSelected === IParcelTypes.Puh,
    );

  const hasAnyEmailReceiver =
    entities.find((e) => e.parcelTypeSelected === IParcelTypes.Email) ||
    cc.find((e) => e.parcelTypeSelected === IParcelTypes.Email);

  const hasAnyCourtParcelReceiver =
    entities.find(
      (e) => e.parcelTypeSelected === IParcelTypes.CourtPortalWriting,
    ) ||
    cc.find((e) => e.parcelTypeSelected === IParcelTypes.CourtPortalWriting);

  const hasAnyNotaReceiver =
    entities.find((e) => e.parcelTypeSelected === IParcelTypes.NotaEncrypted) ||
    cc.find((e) => e.parcelTypeSelected === IParcelTypes.NotaEncrypted);

  const hasAnyPuhReceiver =
    entities.find((e) => e.parcelTypeSelected === IParcelTypes.Puh) ||
    cc.find((e) => e.parcelTypeSelected === IParcelTypes.Puh);

  // EBI — szyfrowane jak przesyłka Noty; klucz obiektu dostaje też bieżące klucze petentów.
  const ebiReceivers = [...entities, ...cc].filter(
    (e) => e.parcelTypeSelected === IParcelTypes.Ebi,
  );

  // Ustawienia wysyłki PUH (hybrydowa) — jeden zestaw dla całej wysyłki.
  const [hybridSettings, setHybridSettings] = useState<IHybridSettings>(
    defaultHybridSettings(),
  );

  const externalEDeliveryFilesSizeLimit = 500 * 1024 * 1024;
  const emailFilesSizeLimit = 50 * 1024 * 1024;

  const {
    files,
    setFiles,
    addNewFile,
    removeFile,
    size: filesSize,
  } = useFiles();
  const inputFile = useRef<HTMLInputElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isCourtPortalParcel, setIsCourtPortalParcel] = useState(false);

  const {
    modals,
    setModals,
    options: modalOptions,
  } = useContext(ModalsManagerContext);
  const { mutate: createParcel } = parcelService.useCreateOutGoingParcel();
  const { data: parcelData } = parcelService.useGetOutcomingInit();

  const { data: writingTypesData } =
    parcelService.useGetCourtPortalWritingTypes();
  const { data: writingAttachmentTypesData } =
    parcelService.useGetCourtPortalWritingAttachmentTypes();

  const parcelInit = useMemo(
    () =>
      parcelData?.data ?? {
        SupportsEDelivery: false,
        SupportsEmail: false,
        SupportsEbi: false,
      },
    [parcelData],
  );

  const writingTypes = useMemo(
    () => writingTypesData?.data ?? [],
    [writingTypesData],
  );

  const writingAttachmentTypes = useMemo(
    () =>
      writingAttachmentTypesData?.data
        ? [
            { id: -1, description: "Pismo procesowe", type: "main" },
            ...(writingAttachmentTypesData?.data ?? []),
          ]
        : null,
    [writingAttachmentTypesData],
  );

  const handleAttachmentTypeChanged = (idx: number, val: string) => {
    const newFiles = [...files];
    const toUpdate = newFiles[idx];
    toUpdate.writingType = val;
    newFiles.splice(idx, 1, toUpdate);
    setFiles(newFiles);
    console.log("Attachment type changed", idx, val);
  };

  const [writingType, setWritingType] = useState<string>(null);
  const onWritingTypeChanged = (value: string) => {
    setWritingType(value);
  };

  const [accounts, setAccounts] = useState<IOutgoingAccount[]>([]);
  const [selectedEmailAccount, setSelectedEmailAccount] =
    useState<IOutgoingAccount>(null);

  const [selectEmailAccountExpanded, setSelectedEmailAccountExpanded] =
    useState<boolean>(false);

  const handleSignFile = (index: number) => {
    setFileToSign(files[index]);

    //handleSign(files[index]);
    setShowCertificates(true);
  };

  const handleSignUploadedFile = (index: number) => {
    setFileToSign(uploadedFiles[index]);

    //handleSign(uploadedFiles[index]);
    setShowCertificates(true);
  };

  const handleSign = async (certificate: ICertificate) => {
    if (!fileToSign) return;

    setShowCertificates(false);

    const b =
      "file" in fileToSign
        ? ((await blobToBase64(fileToSign.file)) as string)
        : ((await blobToBase64(fileToSign)) as string);

    signDocument(
      {
        CertificateSerialNumber: certificate.SerialNumber,
        Content: b.split(",")[1],
        ContentType:
          "fileType" in fileToSign
            ? fileToSign.fileType
            : (fileToSign as FileExt).type,
        FileName: fileToSign.name,
        GenerateReport: false,
        ForceXades: true,
      },
      {
        onError: () => {
          setFileToSign(null);
        },
        onSuccess: async (data: any) => {
          const fileContent = data.data.Content;
          const xades = data.data.IsXades;

          const buffer = await readFileFromBase64(
            fileContent,
            xades ? "text/xml" : "application/pdf",
          );
          const blob = new Blob([buffer]);

          fileToSign.xades = blob;

          if ("type" in fileToSign) {
            const idx = files.indexOf(fileToSign);

            console.log("Replacing file", idx);
            const newFiles = [...files];
            newFiles.splice(idx, 1, fileToSign);
            setFiles(newFiles);
          }
        },
      },
    );
  };

  useEffect(() => {
    if (parcelData?.data) {
      setAccounts(parcelData.data.Accounts);
      setSelectedEmailAccount(
        parcelData.data.Accounts.find(
          (x) => x.ParcelType === EParcelType.Email,
        ),
      );
    }
  }, [parcelData]);

  const [showDossier, setShowDossier] = useState<boolean>(false);
  const [showFullDossier, setShowFullDossier] = useState<boolean>(false);
  const [showScan, setShowScan] = useState<boolean>(false);
  const [reply, setReply] = useState({
    id: "",
    kind: "",
    all: false,
  });
  const [forward, setForward] = useState({
    id: "",
    kind: "",
  });
  const [isForward, setIsForward] = useState(false);
  const [type, setType] = useState<string | null>(null);


  const getValidationErrors = () => {
    console.log("Get validation errors", files);
    if (!title.length) {
      return t("packages.enterParcelTitle");
    }

    if (!entities.length) {
      return t("packages.chooseRecipients");
    }

    if (isCourtPortalParcel) {
      if (writingType == null || !writingType.length) {
        return t("packages.enterParcelType");
      }

      if (!files.find((x) => x.writingType === "main")) {
        return t("packages.processingDocumentRequired");
      }
    }

    // PUH (hybrydowa) idzie do wydruku i nadania jako list polecony — bez treści tekstowej,
    // więc musi mieć co najmniej jeden załącznik (inaczej Poczta nadałaby pusty, płatny list).
    if (hasAnyPuhReceiver && !files.length) {
      return "Przesyłka hybrydowa (PUH) wymaga co najmniej jednego załącznika — pismo zostanie wydrukowane i nadane listownie.";
    }

    return "";
  };

  const isDisabled = () => {
    if (!title.length) {
      return true;
    }

    if (!entities.length) {
      return true;
    }

    for (const f of files) {
      if (!validateAttachment(f)) return true;
    }

    if (isCourtPortalParcel) {
      if (writingType == null || !writingType.length) {
        return true;
      }

      if (!files.find((x) => x.writingType === "main")) {
        return true;
      }
    }

    // PUH wymaga załącznika (patrz getValidationErrors) — blokuj wysyłkę bez pliku.
    if (hasAnyPuhReceiver && !files.length) {
      return true;
    }

    return false;
  };

  useOnClickOutside(outSideRef, () => {
    if (showAddAttachmentsDropdown) {
      setShowAddAttachmentsDropdown(false);
    }
  });

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const validateAttachment = (file: File) => {
    const ext = file.name.split(".").pop();

    if (hasAnyExternalEDelvieryReceiver && !isAllowedEDeliveryAttachment(ext)) {
      return false;
    }

    if (!isAttachmentNameValid(file.name)) {
      return false;
    }

    if (
      hasAnyExternalEDelvieryReceiver &&
      filesSize > externalEDeliveryFilesSizeLimit
    )
      return false;
    if (hasAnyEmailReceiver && filesSize > emailFilesSizeLimit) return false;

    return true;
  };

  const getAttachmentErrorMessage = (file: File) => {
    const ext = file.name.split(".").pop();

    if (hasAnyExternalEDelvieryReceiver && !isAllowedEDeliveryAttachment(ext)) {
      return t("errors.invalidAttachmentFormat");
    }

    if (!isAttachmentNameValid(file.name)) {
      return t("errors.attachmentNameTooLong");
    }

    if (
      hasAnyExternalEDelvieryReceiver &&
      filesSize > externalEDeliveryFilesSizeLimit
    )
      return t("errors.eDeliverySizeLimit");

    if (hasAnyEmailReceiver && filesSize > emailFilesSizeLimit)
      return t("errors.emailSizeLimit");

    return null;
  };

  useEffect(() => {
    setFiles([...files]);
  }, [entities, cc]);

  useEffect(() => {
    if (isCourtPortalParcel && files != null && files.length > 0) {
      const anyMain = files.find((x) => x.writingType === "main");

      if (!anyMain) {
        const newFiles = [...files];
        if (!newFiles[0].writingType) {
          newFiles[0].writingType = "main";

          setFiles(newFiles);
        }
      }
    }
  }, [isCourtPortalParcel, files]);


  // W zależności od tego czy wybrano sprawę
  // zwraca klucz publiczny użytkownika lub sprawy
  // którym zostanie zaszyfrowany klucz prywatny przesyłki
  const getUserOrCasePublicKey = async (): Promise<openpgp.key.Key> => {
    if (c) {
      const response = await axiosInstance.get(`/case/keys?id=${c?.Id}`);
      // Publiczny klucz sprawy
      const pk_base64 = response.data?.[0].PublicKey;

      const decryptedPublicKey = await readFileFromBase64(
        pk_base64,
        "application/other",
      );

      const key = await openpgp.key.read(new Uint8Array(decryptedPublicKey));

      return key.keys[0];
    }

    const myKeys = await getKeys(userId);

    const masterPublicKey = await readFileFromBase64(
      myKeys!.publicBase64Key,
      "application/other",
    );
    const armored = new TextDecoder().decode(masterPublicKey);

    const t = await openpgp.key.readArmored(armored);

    return t.keys[0];
  };

  const getThreadPublicKey = async (threadId: string): Promise<string> => {
    if (threadId) {
      try {
        const response = await axiosInstance.get(`/thread/key?id=${threadId}`);
        // Publiczny klucz sprawy
        const pk_base64 = response.data?.PublicKey;

        return pk_base64;
      } catch (error) {
        return null;
      }
      // const decryptedPublicKey = await readFileFromBase64(
      //   pk_base64,
      //   "application/other",
      // );

      // var key = await openpgp.key.read(new Uint8Array(decryptedPublicKey));

      // return key.keys[0];
    }
    return null;
  };

  const getEntityId = (entity: IEntity) => {
    // id to albo Participant.Id, które jest intem lub UserId, które jest zarówno w UserId
    // więc jeżeli Id nie konwertuje się to int a userId jest puste to bieżemy Id
    // jeżeli userId nie jest puste to userId
    // w przeciwnym przypadku null
    // UwAGA: na potrzeby usuwania i rozróżniania w nowych pozycjach w ID jest losowy string

    if (isNumber(entity.Id)) {
      return String(entity.Id);
    }

    if (entity.UserId) {
      return entity.UserId;
    }

    return undefined;
  };

  const handleUpdateProgressFile = (
    allFiles: FileExt[],
    fileIndex: number,
    progress: number,
  ) => {
    const newFiles = [...allFiles];

    const f = newFiles[fileIndex];
    if (f) {
      f.progress = progress;
      setFiles(newFiles);
    }
  };

  const handleSend = async () => {
    setIsLoading(true);

    const publicKey = await getUserOrCasePublicKey();
    const myKeys = await getKeys(userId);
    const isEncryptedParcel =
      entities.find(
        (entity) =>
          // entity.parcelTypeSelected === IParcelTypes.EncryptedEDelivery ||
          entity.parcelTypeSelected === IParcelTypes.NotaEncrypted ||
          entity.parcelTypeSelected === IParcelTypes.Ebi,
      ) ??
      cc.find(
        (entity) =>
          // entity.parcelTypeSelected === IParcelTypes.EncryptedEDelivery ||
          entity.parcelTypeSelected === IParcelTypes.NotaEncrypted ||
          entity.parcelTypeSelected === IParcelTypes.Ebi,
      );

    const isNormalParcel =
      entities.find(
        (entity) =>
          entity.parcelTypeSelected === IParcelTypes.ExternalEDelivery ||
          entity.parcelTypeSelected === IParcelTypes.Puh ||
          entity.parcelTypeSelected === IParcelTypes.Epuap ||
          entity.parcelTypeSelected === IParcelTypes.Email ||
          entity.parcelTypeSelected === IParcelTypes.TraditionalLetter,
      ) ??
      cc.find(
        (entity) =>
          entity.parcelTypeSelected === IParcelTypes.ExternalEDelivery ||
          entity.parcelTypeSelected === IParcelTypes.Puh ||
          entity.parcelTypeSelected === IParcelTypes.Email ||
          entity.parcelTypeSelected === IParcelTypes.TraditionalLetter,
      );

    const emailParcels =
      entities.find(
        (entity) => entity.parcelTypeSelected === IParcelTypes.Email,
      ) ??
      cc.find((entity) => entity.parcelTypeSelected === IParcelTypes.Email);

    // const isWorkspaceForwared = entities.find(
    //   (x) =>
    //     x.FromWorkspace && x.parcelTypeSelected === IParcelTypes.NotaEncrypted
    // );

    // const isWorkspaceShared = cc.find(
    //   (x) =>
    //     x.FromWorkspace && x.parcelTypeSelected === IParcelTypes.NotaEncrypted
    // );

    // todo: obsługa mixu - przekazywanie i wysłanie
    // if (
    //   ((forward && forward.id) || (reply && reply.id)) &&
    //   (isWorkspaceForwared || isWorkspaceShared)
    // ) {
    //   handlePassOrShare();
    //   return;
    // }

    logger.log(
      `Creating parcel, hasEncrypted: ${!!isEncryptedParcel}, hasNormal: ${!!isNormalParcel}, hasEmail: ${!!emailParcels}`,
    );

    if (!parcelInit.SupportsEmail && emailParcels) {
      notification({
        type: ENotificationType.ERROR,
        title: t("errors.error"),
        text: t("errors.noMailboxConfigured"),
      });
      setIsLoading(false);
      return;
    }

    let threadKeys: string[] = [];

    if (sourcePack && sourcePack?.Thread?.Id) {
      const th = await getThreadPublicKey(sourcePack?.Thread?.Id);

      if (th) threadKeys = [th];
    }

    // Bieżące klucze petentów EBI (bez klucza — przesyłka poczeka na przeszyfrowanie w urzędzie) i klucz FIRMY:
    // przeszyfrowanie dla petenta (KeysManager) robi przeglądarka dowolnego uprawnionego pracownika, nie tylko
    // nadawcy, więc klucz obiektu musi dać się odszyfrować kluczem firmy.
    let ebiKeys: string[] = [];

    if (ebiReceivers.length) {
      try {
        const participantIds = ebiReceivers.map((e) => Number(getEntityId(e))).filter((id) => id > 0);
        const recipientKeys = await ebiOfficeService.getRecipientKeys(participantIds);

        // Tylko adresaci z zapytania i jeden klucz na adresata — nadmiarowa pozycja odpowiedzi nie dopisze odbiorcy.
        const keyByParticipant = new Map<number, string>();

        recipientKeys.forEach((k) => {
          if (participantIds.includes(k.ParticipantId) && k.PublicKey && !keyByParticipant.has(k.ParticipantId)) {
            keyByParticipant.set(k.ParticipantId, k.PublicKey);
          }
        });

        ebiKeys = [await getCompanyPublicKeyBase64(), ...keyByParticipant.values()];
      } catch (e) {
        setIsLoading(false);
        notification({
          type: ENotificationType.ERROR,
          title: t("packages.parcelSubmitFailed"),
          text: getErrorMessage(e as IError),
        });
        return;
      }
    }

    // Klucz obiektu dla przesyłki
    const parcelKey = await generateSingleBinaryFromMultiplePublicKeys([
      ...ebiKeys,
      ...entities
        .map((entity) => entity.PublicKey ?? "")
        .filter((e) => !!e.length),
      ...cc.map((entity) => entity.PublicKey ?? "").filter((e) => !!e.length),
      myKeys!.publicBase64Key,
      ...threadKeys,
    ]);

    // D1: powiązanie klucza przesyłki z doręczeniem EBI, podpisane kluczem firmy — przeglądarka urzędu przeszyfruje go
    // petentowi tylko z ważnym podpisem. Bez klucza firmy w tej karcie przesyłki EBI nie nadamy.
    const ebiBindings = new Map<string, IEbiKeyBindingFields>();

    try {
      for (const entity of ebiReceivers) {
        const id = getEntityId(entity);

        if (id && !ebiBindings.has(id)) {
          ebiBindings.set(id, await signEbiKeyBinding(parcelKey.cleanPublicKey.keys[0], Number(id)));
        }
      }
    } catch (e) {
      setIsLoading(false);
      notification({
        type: ENotificationType.ERROR,
        title: t("packages.parcelSubmitFailed"),
        text: (e as Error)?.message || t("errors.somethingWentWrong"),
      });
      return;
    }

    const ebiBindingFor = (entity: IEntity, id: string | undefined) =>
      entity.parcelTypeSelected === IParcelTypes.Ebi && id ? ebiBindings.get(id) : undefined;

    // Szyfrowanie klucza kluczem sprawy
    let parcelKey_encryptedWithSenderCase: string = null;
    if (c) {
      parcelKey_encryptedWithSenderCase = await encryptPrivateKey(
        parcelKey,
        publicKey,
      );
    }

    const sendContent = cleanHTML(content);

    logger.log("Encrypting content");
    // Szyfrowanie treści
    const encryptedContent = await encryptContent(
      sendContent,
      parcelKey.cleanPublicKey,
    );

    let normalUri: any = [];
    let normalBlobUri: any = [];
    let encryptedUri: any = [];
    let encryptedBlobUri: any = [];

    if (isNormalParcel || emailParcels) {
      if (files.length) {
        logger.log("Creating unencrypted files from disk");
        console.time("handleCreate");
        normalUri = await handleCreate(
          files,
          c?.Id ?? "",
          parcelKey.cleanPublicKey,
          false,
          false,
          null,
          null,
          (fileIndex: number, progress: number) => {
            handleUpdateProgressFile(files, fileIndex, progress);
          },
        );
        console.timeEnd("handleCreate");
      }

      if (uploadedFiles.length) {
        logger.log("Creating unencrypted files from existing files");
        console.time("uploadBlob");
        normalBlobUri = await uploadBlob(
          uploadedFiles,
          "",
          parcelKey,
          false,
          false,
        );
        console.timeEnd("uploadBlob");
      }
    }

    if (isEncryptedParcel) {
      if (files.length) {
        logger.log("Creating encrypted files from disk");
        console.time("handleCreate");
        encryptedUri = await handleCreate(
          files,
          c?.Id ?? "",
          parcelKey.cleanPublicKey,
          true,
          true,
          null,
          null,
          (fileIndex: number, progress: number) => {
            handleUpdateProgressFile(files, fileIndex, progress);
          },
          false,
        );
        console.timeEnd("handleCreate");
      }

      if (uploadedFiles.length) {
        logger.log("Creating unencrypted files from existing files");
        console.time("uploadBlob");
        encryptedBlobUri = await uploadBlob(
          uploadedFiles,
          "",
          parcelKey,
          true,
          true,
        );
        console.timeEnd("uploadBlob");
      }
    }

    const uris = [
      normalUri,
      normalBlobUri,
      encryptedUri,
      encryptedBlobUri,
    ].flat();

    logger.log("Making request to BE");

    createParcel(
      {
        Attachments: uris.length
          ? uris?.map((url, index) => ({
              Encrypted: url!.encrypted,
              Order: index,
              Uri: url!.uri,
            }))
          : [],

        //@ts-ignore
        CaseId: c ? c.Id : null,
        Cc: cc.map((entity) => {
          const id = getEntityId(entity);

          return {
            Id: id,
            Type: entity.Type,
            Parcel:
              entity.Id !== -1
                ? (convertParcelTypeToKind(entity.parcelTypeSelected!) as any)
                : 8,
            Email: entity.Email,
            EDeliveryAddress: entity.EDeliveryAddress,
            PublicKey: isEncryptedParcel ? parcelKey.PublicKey : null,
            PrivateKey: isEncryptedParcel ? parcelKey.PrivateKey : null,
            ...ebiBindingFor(entity, id),
          };
        }),
        Reply: reply.id.length
          ? { Id: reply.id, Kind: reply.kind }
          : undefined,
        Forward: forward.id.length
          ? { Id: forward.id, Kind: forward.kind }
          : undefined,
        Content: isEncryptedParcel
          ? await readAsBase64(encryptedContent)
          : sendContent,
        RawContent: isNormalParcel ? sendContent : null,
        DocumentId: null,
        PrivateKey: isEncryptedParcel
          ? parcelKey_encryptedWithSenderCase ?? parcelKey.PrivateKey
          : null,
        OriginalPrivateKey: isEncryptedParcel ? parcelKey.PrivateKey : null,
        PublicKey: isEncryptedParcel ? parcelKey.PublicKey : null,
        Receivers: entities
          .filter((entity) => !entity.cc)
          .map((entity) => {
            const id = getEntityId(entity);

            return {
              Id: id,
              Type: entity.Type,
              Parcel: convertParcelTypeToKind(entity.parcelTypeSelected!),
              Email: entity.Email,
              EDeliveryAddress: entity.EDeliveryAddress,
              PublicKey: isEncryptedParcel ? parcelKey.PublicKey : null,
              PrivateKey: isEncryptedParcel ? parcelKey.PrivateKey : null,
              ContactMessageChatId: entity.ContactMessageChatId,
              ...ebiBindingFor(entity, id),
            };
          }),
        Title: title,
        HybridSettings: hasAnyPuhReceiver ? hybridSettings : undefined,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          setModals({ ...modals, createPost: false });
        },
        onError(e: any) {
          setIsLoading(false);
          notification({
            title: t("packages.parcelSubmitFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  // const handlePassOrShare = async () => {
  //   const myKeys = await getKeys(userId!);
  //   let id: null | string = null;
  //   let Kind: null | ItemKind = null;
  //   let encryptionKeyId: string | null = null;
  //   if (sourcePack) {
  //     id = sourcePack.Id;
  //     Kind = sourcePack.Kind;
  //     encryptionKeyId = sourcePack.EncryptionKeyId;
  //   }

  //   const passTo = entities.find(
  //     (x) =>
  //       x.FromWorkspace && x.parcelTypeSelected === IParcelTypes.NotaEncrypted
  //   );

  //   const shareTo = cc.find(
  //     (x) =>
  //       x.FromWorkspace && x.parcelTypeSelected === IParcelTypes.NotaEncrypted
  //   );

  //   let sendContent = cleanHTML(content);

  //   if (!encryptionKeyId && passTo) {
  //     redirect(
  //       {
  //         Id: id!,
  //         Kind: Kind!,
  //         ToUserId: passTo.UserId,
  //         Title: title,
  //         Content: sendContent,
  //       },
  //       {
  //         onSuccess: () => {
  //           setIsLoading(false);
  //           setModals({ ...modals, createPost: false });

  //           if (sourcePack) {
  //             let newPacks = [...packages];
  //             let newPack = newPacks.filter((p) => p.Id !== sourcePack.Id);
  //             setPackages(newPack);
  //           }
  //         },
  //       }
  //     );
  //     return;
  //   }

  //   if (!encryptionKeyId && shareTo) {
  //     await axiosInstance.post("/message/share", {
  //       Id: id!,
  //       Kind: Kind!,
  //       ToUserId: shareTo.UserId,
  //       Title: title,
  //       Content: sendContent,
  //     });
  //     setIsLoading(false);
  //     setModals({ ...modals, createPost: false });
  //     return;
  //   }

  //   if (!encryptionKeyId) return;

  //   //const privateKey = await generateNewKey(encryptionKeyId, myKeys, keys);
  //   const encryptionKey = keys.find((k) => k?.Id === encryptionKeyId);

  //   const uint = await reEncrypt(
  //     userId,
  //     passTo ? passTo.UserId : shareTo.UserId,
  //     encryptionKey
  //   );
  //   const readyToSend = await readAsBase64(uint);

  //   if (passTo) {
  //     redirect(
  //       {
  //         Id: id!,
  //         Kind: Kind!,
  //         ToUserId: passTo.UserId,
  //         Title: title,
  //         Content: sendContent,
  //         Keys: [{ Id: encryptionKeyId!, Key: readyToSend }],
  //       },
  //       {
  //         onSuccess: () => {
  //           if (sourcePack) {
  //             let newPacks = [...packages];
  //             let newPack = newPacks.filter((p) => p.Id !== sourcePack.Id);
  //             setPackages(newPack);
  //           }

  //           setIsLoading(false);
  //           setModals({ ...modals, createPost: false });
  //         },
  //       }
  //     );
  //   }

  //   if (shareTo) {
  //     await axiosInstance.post("/message/share", {
  //       Id: id!,
  //       Kind: Kind!,
  //       ToUserId: shareTo.UserId,
  //       Title: title,
  //       Content: sendContent,
  //       Keys: [{ Id: encryptionKeyId!, Key: readyToSend }],
  //     });
  //     setIsLoading(false);
  //     setModals({ ...modals, createPost: false });
  //   }
  // };

  useEffect(() => {
    if (entities) {
      const cpParcel = entities.find(
        (x) => x.parcelTypeSelected === IParcelTypes.CourtPortalWriting,
      );
      setIsCourtPortalParcel(!!cpParcel);
    }
  }, [entities]);

  const handleChooseFile = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();

    for (const file of (event.target as any).files) {
      const ext = (file as File).name.split(".").pop();

      if (
        hasAnyExternalEDelvieryReceiver &&
        !isAllowedEDeliveryAttachment(ext)
      ) {
        setErrorMessage({
          title: t("errors.invalidAttachmentFormat"),
          message: t("errors.fileInvalidFormat", { name: (file as File).name }),
          hint: t("errors.supportedFormats"),
        });
        return;
      }
    }

    for (const file of (event.target as any).files) {
      addNewFile(file);
    }
  };

  const handleClickToUpload = () => {
    if (!inputFile.current) return;
    inputFile.current.click();
  };

  const [uploadedFiles, setUploadedFiles] = useState<IUploadedFile[]>([]);
  const [showAddAttachmentsDropdown, setShowAddAttachmentsDropdown] =
    useState(false);

  const handleAddFiles = (files: IUploadedFile[]) => {
    setUploadedFiles([...uploadedFiles, files].flat());
    setShowDossier(false);
  };

  const removeUploadedFile = (sindex: number) => {
    setUploadedFiles(uploadedFiles.filter((a, index) => index !== sindex));
  };

  useEffect(() => {
    const elements = localStorage.getItem("send_item");

    if (elements?.length) {
      localStorage.removeItem("send_item");
    }
  }, []);

  const [showCase, setShowCase] = useState(false);

  useEffect(() => {
    const email = sessionStorage.getItem("reply");

    if (!email || !email?.length) return;

    const data = JSON.parse(email);
    const t = sessionStorage.getItem("type");

    setTitle(
      data.title.toLowerCase().startsWith("re:")
        ? data.title
        : `Re: ${data.title}`,
    );

    if (data.case) {
      setCase(data.case);
    }

    setReply({ id: data.id, kind: data.kind, all: t === "replyall" });
    sessionStorage.removeItem("reply");
  }, []);

  const formatOriginalMessage = (
    message: string,
    pack: IPackage,
    isForward: boolean,
  ) => {
    // const formattedMessage = message
    //   .split('\n') // Split by newline
    //   .map(line => `&gt; ${line}`) // Prefix each line with '>'
    //   .join('<br>'); // Join with <br> to preserve line breaks

    // Wrap in a blockquote-style div to simulate email reply formatting
    if (!isForward) {
      return `
      <div id="divRplyFwdMsg" class="moz-cite-prefix">
      <p>${t("packages.replyHeader", { date: moment(pack.SendDate).format("LLL"), sender: pack.Sender })}</p>
      <div style="color: #555; font-size: 0.9em; padding-left: 1rem; border-left: 2px solid #ccc;">
        ${message}
      </div>
      </div>
    `;
    } else {
      return `
      <div id="divRplyFwdMsg"  class="moz-cite-prefix">
      <p>--- ${t("packages.forwardedMessageContent")} ---</p>
      <table cellpadding="0" cellspacing="0" border="0"
        class="moz-email-headers-table">
        <tbody>
          <tr>
            <th valign="BASELINE" align="RIGHT" nowrap="nowrap">${t("packages.subject")}: </th>
            <td>${pack.S1}</td>
          </tr>
          <tr>
            <th valign="BASELINE" align="RIGHT" nowrap="nowrap">${t("common.date")}: </th>
            <td>${moment(pack.SendDate).format("LLL")}</td>
          </tr>
          <tr>
            <th valign="BASELINE" align="RIGHT" nowrap="nowrap">${t("packages.senderHeader")}:
            </th>
            <td>${pack.Sender.replace("<", "&lt;").replace(">", "&gt;")}</td>
          </tr>
          <tr>
            <th valign="BASELINE" align="RIGHT" nowrap="nowrap">${t("packages.recipientHeader")}:
            </th>
            <td>${pack.Receivers.filter((r) => !r.Cc && !r.Bcc)
              .map((x) =>
                x.Name
                  ? `${x.Name} &lt;${x.Email ?? x.EDeliveryAddress}&gt;`
                  : `${x.Email ?? x.EDeliveryAddress}`,
              )
              .join(", ")}</td>
          </tr>
          <tr>
            <th valign="BASELINE" align="RIGHT" nowrap="nowrap">${t("packages.ccHeader")}: </th>
            <td>${pack.Receivers.filter((r) => r.Cc)
              .map((x) =>
                x.Name != null ? `${x.Name} &lt;${x.Email}&gt;` : `${x.Email}`,
              )
              .join(", ")}</td>
          </tr>
        </tbody>
      </table>
      <br/>
      <div style="color: #555; font-size: 0.9em; padding-left: 1rem; border-left: 2px solid #ccc;">
        ${message}
      </div>
      </div>
    `;
    }
  };
  const pId = forward?.id ? forward.id : reply.id;
  const pKind = forward?.kind ? forward.kind : reply.kind;

  const { data: previewData, isLoading: isLoadingEmailContent } =
    EmailService.useGetPreview(
      pId ?? "",
      true,
      Number(pKind) === 128 ? true : false,
    );

  const { keys } = useContext(KeysContext);

  function isBase64(str: string) {
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  }

  useEffect(() => {
    if (!forward.id && !reply.id) {
      setContent("<p></p>" + (footerData?.data?.Content ?? ""));
    }
  }, [footerData]);

  useEffect(() => {
    if (forward.id || reply.id) {
      setIsLoadingContent(true);
      loadContentOfReplayOrForwardMessage()
        .then(() => setIsLoadingContent(false))
        .catch(() => setIsLoadingContent(false));
    }
  }, [reply, forward, previewData]);

  const decodeEncryptedContent = async () => {
    const objectKey = previewData?.data?.ObjectKeys?.[0] as IKey;

    let parentKey = null;

    if (objectKey.ParentKeyId) {
      parentKey = previewData?.data.Keys.find(
        (k: IKey) => k?.Id === objectKey?.ParentKeyId,
      );
    }

    const content = await decryptPackage(
      userId,
      previewData?.data.Content!,
      objectKey,
      parentKey ?? undefined,
    );

    // const images = await decodeAndFetchImages(
    //   userId,
    //   previewData?.data,
    //   objectKey,
    //   parentKey ?? undefined
    // );

    return content;
  };

  const loadContentOfReplayOrForwardMessage = async () => {
    const { data } = await axiosInstance.get(`/list?Id=${pId}&Kind=${pKind}`);

    const pack: IPackage = data.Items[0];
    setSourcePack(pack);

    // Licznik tymczasowych ujemnych Id dla budowanych adresatów (oryginalnie `var id = -1`
    // z hoistingiem; po cleanupie przywrócone jako jawna deklaracja na początku funkcji).
    let id = -1;

    if (pack.Case) {
      setCase(pack.Case);
    }

    if (!pack) return;

    if (pack.Kind === ItemKind.OutgoingParcel) {
      const to: IEntity[] = [
        {
          Id: id--,
          Email: pack.SenderDetails ? pack.SenderDetails.Email : pack.Sender,
          Label: pack.SenderDetails
            ? pack.SenderDetails.Name ?? pack.SenderDetails?.Email
            : pack.Sender,
          UserId: pack.SenderUserId,
          Type: EEntityType.NotaParticipant,
          ParcelTypes: EParcelType.NotaEncrypted,
          parcelTypeSelected: IParcelTypes.NotaEncrypted,

          Address: "",
          BuildingNumber: "",
          City: "",
          Claims: [],
          Description: "",
          EDeliveryAddress: "",
          FirstName: "",
          FlatNumber: "",
          IsGlobal: false,
          KRS: "",
        },
      ];

      if (!isForward) {
        setInitialEntities(to);
        setEntities(to);
      }

      return;
    }

    if (pack.Kind === ItemKind.Email || pack.Kind === ItemKind.ContactMessage) {
      const isEncrypted = previewData?.data?.IsEncrypted;

      let id = -1;

      if (!isForward) {
        const to: IEntity[] = [
          {
            Id: id--,
            Email: pack.SenderDetails ? pack.SenderDetails.Email : pack.Sender,
            Label: pack.SenderDetails
              ? pack.SenderDetails.Name ?? pack.SenderDetails?.Email
              : pack.Sender,
            Type: pack.SenderDetails?.ContactMessageChatId
              ? EEntityType.ContactMessageSender
              : EEntityType.Email,
            ParcelTypes: pack.SenderDetails?.ContactMessageChatId
              ? EParcelType.NotaEncrypted
              : EParcelType.Email,
            parcelTypeSelected: pack.SenderDetails?.ContactMessageChatId
              ? IParcelTypes.NotaEncrypted
              : IParcelTypes.Email,

            Address: "",
            BuildingNumber: "",
            City: "",
            Claims: [],
            Description: "",
            EDeliveryAddress: "",
            FirstName: "",
            FlatNumber: "",
            IsGlobal: false,
            KRS: "",
            PublicKey: pack.SenderDetails?.PublicKey,
            ContactMessageChatId: pack.SenderDetails?.ContactMessageChatId,
          },
        ];

        if (reply.all) {
          to.push(
            ...pack.Receivers.filter(
              (r) =>
                !r.Cc &&
                !r.Bcc &&
                !r.IsMe &&
                r.ParcelType !== EParcelType.ContactMessage,
            ).map((r) => ({
              Id: id--,
              Email: r.Email,
              Label: r.Name ?? r.Email,
              Type: EEntityType.Email,
              ParcelTypes: EParcelType.Email,
              parcelTypeSelected: IParcelTypes.Email,

              Address: "",
              BuildingNumber: "",
              City: "",
              Claims: [],
              Description: "",
              EDeliveryAddress: "",
              FirstName: "",
              FlatNumber: "",
              IsGlobal: false,
              KRS: "",
            })),
          );
        }

        to.forEach((t) => {
          fillIcon(t);
        });

        setInitialEntities(to);
        setEntities(to);

        if (reply.all) {
          const cc = pack.Receivers.filter(
            (r) =>
              r.Cc && !r.IsMe && r.ParcelType !== EParcelType.ContactMessage,
          ).map((r) => ({
            Id: id--,
            Email: r.Email,
            Label: r.Name ?? r.Email,
            Type: EEntityType.Email,
            ParcelTypes: EParcelType.Email,
            parcelTypeSelected: IParcelTypes.Email,
          }));

          setInitialCC(cc);
          setCC(cc);
          if (cc.length > 0) {
            setShowDW(true);
          }
        } else {
          setCC([]);
        }
      }

      if (isEncrypted) {
        decodeEncryptedContent().then((r) => {
          setContent(
            `${
              footerData?.data?.Content
                ? "<p></p>" +
                  footerData?.data?.Content +
                  "<p></p><p></p><p></p>"
                : ""
            }<p></p><p></p><details><summary>${t("common.history")} <i style="font-size:11px">&#x25BC</i></summary>${formatOriginalMessage(
              r,
              pack,
              isForward,
            )}</details>`,
          );
        });
        return;
      }

      if (!previewData) {
        return;
      }

      const isImages = await hasImages(previewData?.data?.Content);

      setContent(
        await addToUrl(previewData?.data?.Content, isImages ? true : false),
      );

      return;
    }

    try {
      atob(pack.S2);
      const encryptionKey = keys.find(
        (key) => key?.Id === pack?.EncryptionKeyId,
      );

      let parentKey = null;
      if (encryptionKey?.ParentKeyId) {
        parentKey = keys.find((key) => key?.Id === encryptionKey.ParentKeyId);
      }

      const decrypted = await decryptPackage(
        userId,
        pack.S2,
        encryptionKey,
        parentKey ?? undefined,
      );

      setContent(
        `${
          footerData?.data?.Content
            ? "<p></p>" + footerData?.data?.Content + "<p></p><p></p><p></p>"
            : ""
        }<p></p><p></p><details><summary>${t("common.history")} <i style="font-size:11px">&#x25BC</i></summary>${formatOriginalMessage(
          decrypted,
          pack,
          isForward,
        )}</details>`,
      );
    } catch (e) {
      if (isBase64(pack.S2)) {
        setContent("error");
        return;
      }
      setContent(
        `${
          footerData?.data?.Content
            ? "<p></p>" + footerData?.data?.Content + "<p></p><p></p><p></p>"
            : ""
        }<p></p><p></p><details><summary>${t("common.history")} <i style="font-size:11px">&#x25BC</i></summary>${formatOriginalMessage(
          pack.S2,
          pack,
          isForward,
        )}</details>`,
      );
      return;
    }
  };

  const cleanHTML = (htmlString: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Remove <summary> elements
    const summaryElements = doc.querySelectorAll("summary");
    summaryElements.forEach((summary) => {
      summary.remove();
    });

    // Remove <details> elements but preserve children
    const detailsElements = doc.querySelectorAll("details");
    detailsElements.forEach((details) => {
      // Replace <details> with its children (preserving the order)
      while (details.firstChild) {
        details.parentNode.insertBefore(details.firstChild, details);
      }
      details.remove();
    });

    return doc.body.innerHTML;
  };

  const loadAttachmentsOfReplyOrForwardMessage = async () => {
    logger.log("Loading attachments to forward");
    const { data } = await axiosInstance.get(`/list?Id=${pId}&Kind=${pKind}`);
    const pack: IPackage = data.Items[0];
    if (!pack) return;

    const attachments: IAttachment[] = pack.Attachments;

    const files = [];
    for (const element of attachments) {
      const file = await getFile(element.Uri);
      const blob = await getBlob(file.data.Url);
      const key = keys.find((key: any) => key?.Id === pack.EncryptionKeyId);

      if (!key) {
        if (!pack.EncryptionKeyId) {
          const el = {
            file: new Blob([blob.data]),
            name: element.Label ?? element.FileName,
            fileType: element.ContentType,
            size: element.Size,
          };
          files.push(el);
        }
      } else {
        let parentKey = null;
        //@ts-ignore
        if (key.ParentKeyId) {
          parentKey = keys.find(
            //@ts-ignore
            (pkey: any) => pkey?.Id === key?.ParentKeyId,
          );
        }

        const decryptedFile = await decryptFile(
          userId ?? "",
          new Blob([blob.data]),
          key,
          parentKey,
        );

        const el = {
          file: decryptedFile,
          name: element.Label ?? element.FileName,
          fileType: element.ContentType,
          size: element.Size,
        };
        files.push(el);
      }
    }

    handleAddFiles(files);
  };

  useEffect(() => {
    const t = sessionStorage.getItem("type");

    if (t) {
      setType(t);
      sessionStorage.removeItem("type");
    }

    if (t !== "forward") return;

    const email = sessionStorage.getItem("forward");

    if (!email || !email?.length) return;

    const data = JSON.parse(email);

    if (data.case) {
      console.log("ustawiam poprawnie teczke: ", data.case);
      setCase(data.case);
    }

    setIsForward(true);
    setForward({ id: data.id, kind: data.kind });

    // const msg = sessionStorage.getItem("msg") ?? "";
    // console.log("Ustawiam forward content", msg);
    // setContent(msg);
    sessionStorage.removeItem("reply");

    setTitle(
      data.title.toLowerCase().startsWith("fwd:")
        ? data.title
        : `Fwd: ${data.title}`,
    );
  }, []);

  useEffect(() => {
    if (isForward && pId) {
      loadAttachmentsOfReplyOrForwardMessage();
    }
  }, [isForward, pId, pKind]);

  const refWrapper = useRef(null);

  useEffect(() => {
    if (modalOptions) {
      const o = modalOptions as CreateOutgoingParcelModalOptions;
      if (o.case) {
        setCase(o.case);
      }
    }
  }, [modalOptions]);

  const handleRegisterEditor = (editor: Editor) => {
    editor.on("click", (e) => {
      if (e.target?.nodeName === "SUMMARY") {
        editor.execCommand("ToggleAccordion", false);
      }
    });

    // fetch(attachIcon)
    //   .then((response) => response.text())
    //   .then((text) => {
    //     editor.ui.registry.addIcon("attachments", text);
    //     editor.ui.registry.addMenuButton("attachments", {
    //       icon: "attachments",
    //       text: "Załącznik",
    //       fetch: (callback) => {
    //         const items = [
    //           {
    //             type: "menuitem",
    //             text: "Z dysku",
    //             onAction: () => handleClickToUpload(),
    //           },
    //           {
    //             type: "menuitem",
    //             text: "Z zakładki dossier",
    //             onAction: () => {
    //               setShowDossier(true);
    //             },
    //           },
    //         ];

    //         callback(items);
    //       },
    //     });
    //   })
    //   .catch((error) => {
    //     console.error("Error fetching the icon:", error);
    //   });
  };

  const [showDW, setShowDW] = useState(false);
  const inputsRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (inputsRef.current) {
      const position = inputsRef.current.getBoundingClientRect();
      console.log(position);
      setHeight(!isHidden ? inputsRef.current.scrollHeight : 0);
    }
  }, [isHidden, showDW, entities]);

  const handleCloseModal = () => {
    setModals({ ...modals, hide: true });
  };


  const { blob, addBlob, removeBlob } = usePDF();

  const handleShowPreview = (file: File | IUploadedFile) => {
    if (file instanceof File) {
      const newBlob = new Blob([file], { type: file.type });
      addBlob(newBlob);
      return;
    }

    addBlob(file.file, file.name);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!wrapperRef.current) return;

    if (!isAncestor(e.relatedTarget, wrapperRef.current)) {
      console.log("Drag leave");
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    }
  };

  const isAncestor = (node, target) => {
    if (node === target) return false;
    while (node.parentNode) {
      if (node.parentNode === target) return true;
      node = node.parentNode;
    }
    return false;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Required to allow drop
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    console.log("Files dropped:", files);

    for (const file of files) {
      addNewFile(file);
    }
  };

  return (
    <ModalLayout
      name="createPost"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      refWrapper={refWrapper}
      isAnimation
      fullHeight={true}
      widthAsLeftArea={true}
      disableMove={true}
    >
      <>
        <div
          className={styles.wrapper}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          ref={wrapperRef}
        >
          <div className={styles.title}>
            {type ? (
              <>{type === "forward" ? t("packages.forwardTo") : t("packages.replyAction")}</>
            ) : (
              t("packages.newParcel")
            )}
            <div className={styles.icons}>
              <CloseIcon
                className={styles.closeIcon}
                onClick={handleCloseModal}
              />
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.inputs}>
              <div
                ref={inputsRef}
                style={{
                  display: isHidden ? "none" : "block",
                }}
                className={styles.inputsWrapper}
              >
                <Can I="accessModule" a="cases">
                  <div className={styles.inputRow}>
                    <div className={styles.inputControl}>
                      <SearchCasesNew
                        labelPosition="start"
                        variant="standard"
                        settedCase={c}
                        setAddNewEntity={() => {
                          setShowCase(true);
                        }}
                        onChoose={(c) => setCase(c)}
                        disableBorder={true}
                        allowCreateNew={false}
                        disableSettingActiveCase={true}
                      />
                    </div>
                  </div>
                </Can>
                <div className="relative">
                  <div className={`${styles.inputRow}`}>
                    <div
                      className={`${styles.inputLabel} ${
                        entities.length > 0 ? styles.inoutLabelWithChips : ""
                      }`}
                    >
                      {t("packages.toLabel")}
                    </div>
                    <div className={`${styles.inputControl}`}>
                      <NewSearchEntitiesWithCase
                        id="opm_to"
                        isFullscreen={isFullscreen}
                        className={styles.t}
                        placeholder={t("packages.toLabel")}
                        focus={false}
                        onChoose={(e) => {
                          console.log("Zmiana: ", e);
                          setEntities(e);
                        }}
                        showAlways
                        initialEntities={initialEntities}
                        entities={entities}
                        setShowDw={
                          !showDW
                            ? () => {
                                setShowDW(!showDW);
                              }
                            : null
                        }
                        c={c}
                        supportsEbi={!!parcelInit.SupportsEbi}
                      />
                    </div>
                  </div>
                </div>

                <div className="relative">
                  {showDW && (
                    <div className={`${styles.inputRow}`}>
                      <div
                        className={`${styles.inputLabel} ${
                          entities.length > 0 ? styles.inoutLabelWithChips : ""
                        }`}
                      >
                        {t("chat.ccLabel")}
                      </div>
                      <div className={`${styles.inputControl}`}>
                        <NewSearchEntitiesWithCase
                          id="opm_cc"
                          placeholder={t("chat.ccLabel")}
                          focus={true}
                          onChoose={(e) => {
                            setCC(e);
                            setShowDW(true);
                          }}
                          initialEntities={initialCc}
                          entities={cc}
                          c={c}
                          supportsEbi={!!parcelInit.SupportsEbi}
                          onBlur={() => {
                            // window.setTimeout(() => {
                            //   if (cc.length === 0 && !blockHideDW) {
                            //     setShowDW(false);
                            //   }
                            // }, 300);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <div className={`${styles.inputRow}`}>
                    <div
                      className={`${styles.inputLabel} ${
                        entities.length > 0 ? styles.inoutLabelWithChips : ""
                      }`}
                    >
                      {t("chat.from")}
                    </div>
                    <div className={`${styles.fromControl}`}>
                      {hasAnyEmailReceiver && selectedEmailAccount && (
                        <div ref={outSideSelectEmailRef}>
                          <Chip
                            element={
                              <div
                                onClick={() =>
                                  setSelectedEmailAccountExpanded(true)
                                }
                                className={styles.infoChip}
                              >
                                <EmailIcon></EmailIcon>
                                {selectedEmailAccount.Label}
                                <FontAwesomeIcon icon={faCaretDown} />
                              </div>
                            }
                          />
                        </div>
                      )}

                      {hasAnyNotaReceiver && (
                        <div>
                          <Chip
                            element={
                              <div className={styles.infoChip}>
                                <NotaIcon></NotaIcon>
                                {auth.user?.profile.name ?? ""}
                              </div>
                            }
                          />
                        </div>
                      )}

                      {hasAnyCourtParcelReceiver && (
                        <div>
                          <Chip
                            element={
                              <div className={styles.infoChip}>
                                <CourtIcon></CourtIcon>
                                {auth.user?.profile.name ?? ""}
                              </div>
                            }
                          />
                        </div>
                      )}

                      {hasAnyPuhReceiver && (
                        <PuhOptionsPanel
                          value={hybridSettings}
                          onChange={setHybridSettings}
                        />
                      )}

                      <Popper
                        open={selectEmailAccountExpanded}
                        setOpen={setSelectedEmailAccountExpanded}
                        anchorEl={outSideSelectEmailRef?.current}
                      >
                        <div
                          className={classNames(styles.dropdown2, styles.left)}
                        >
                          {accounts
                            .filter((x) => x.ParcelType === EParcelType.Email)
                            .map((x) => (
                              <div
                                onClick={() => {
                                  setSelectedEmailAccount(x);
                                  setSelectedEmailAccountExpanded(false);
                                }}
                                className={styles.box}
                              >
                                {x.Label}
                              </div>
                            ))}
                        </div>
                      </Popper>

                    </div>
                  </div>
                </div>

                {/* <Input
                  label="Od: "
                  value={from}
                  variant="filled"
                  onChange={(text) => setFrom(text)}
                /> */}
                <div className={styles.inputRow}>
                  <div
                    className={`${styles.inputControl} ${styles.dropdownParent}`}
                    ref={outSideRef}
                  >
                    <Input
                      label={t("forms.title")}
                      value={title}
                      variant="standard"
                      labelPosition="start"
                      disableBorder={true}
                      onChange={(text) => setTitle(text)}
                      hideClearButton={true}
                      onRightClick={() => {
                        if (!ability.can("accessModule", "drive")) {
                          handleClickToUpload();
                          return;
                        }
                        setShowAddAttachmentsDropdown(
                          !showAddAttachmentsDropdown,
                        );
                      }}
                      onRightText={t("documents.attachments")}
                    />

                    {showAddAttachmentsDropdown && (
                      <div ref={outSideRef} className={styles.dropdown}>
                        <div
                          onClick={() => {
                            handleClickToUpload();
                            setShowAddAttachmentsDropdown(false);
                          }}
                          className={styles.box}
                        >
                          {t("common.fromLocalDisk")}
                        </div>
                        {c && (
                          <div
                            onClick={() => {
                              if (!c) {
                                notification({
                                  text: t("packages.chooseCaseForDossier"),
                                  title: t("cases.case"),
                                  type: ENotificationType.ERROR,
                                });
                                return;
                              }

                              setShowAddAttachmentsDropdown(false);
                              setShowDossier(true);
                            }}
                            className={styles.box}
                          >
                            {t("packages.fromCaseDossier")}
                          </div>
                        )}
                        <div
                          onClick={() => {
                            setShowAddAttachmentsDropdown(false);
                            setShowFullDossier(true);
                          }}
                          className={styles.box}
                        >
                          {t("common.fromNotaCloud")}
                        </div>
                        <div
                          onClick={() => {
                            setShowAddAttachmentsDropdown(false);
                            setShowScan(true);
                          }}
                          className={styles.box}
                        >
                          {t("scanner.fromScanner")}
                        </div>
                        {/* <div
                          onClick={() => {
                            if (!c) {
                              notification({
                                text: "Wybierz teczkę żeby dodać pisma",
                                title: "Teczka",
                                type: ENotificationType.ERROR,
                              });
                              return;
                            }

                            setShowAddAttachmentsDropdown(false);
                            setShowLetter(true);
                          }}
                          className={styles.box}
                        >
                          Z zakładki pisma
                        </div> */}
                      </div>
                    )}
                  </div>
                </div>

                {isCourtPortalParcel && (
                  <div className={styles.inputRow}>
                    <AutoComplete
                      labelPosition="start"
                      options={writingTypes.map((x) => ({
                        label: x.key,
                        id: x.id.toString(),
                      }))}
                      onChange={() => {}}
                      onChoose={onWritingTypeChanged}
                      variant="standard"
                      label={t("common.type")}
                      disableBorder={true}
                      value=""
                      disableClearable={true}
                    ></AutoComplete>
                  </div>
                )}
              </div>
            </div>

            {height === 0 && (
              <div className={styles.infoChips}>
                {c && (
                  <Chip
                    element={
                      <div
                        onClick={() => setIsHidden(false)}
                        className={styles.infoChip}
                      >
                        <CaseIcon className={styles.infoIcon} /> {c.Title}
                        <FontAwesomeIcon icon={faCaretDown} />
                      </div>
                    }
                  />
                )}

                {!!entities.length && (
                  <Chip
                    element={
                      <div
                        onClick={() => setIsHidden(false)}
                        className={styles.infoChip}
                      >
                        <div className={styles.dot}>{entities.length}</div>Do:
                        <FontAwesomeIcon icon={faCaretDown} />
                      </div>
                    }
                  />
                )}
                {!!cc.length && (
                  <Chip
                    element={
                      <div
                        onClick={() => setIsHidden(false)}
                        className={styles.infoChip}
                      >
                        <div className={styles.dot}>{cc.length}</div>Dw:
                        <FontAwesomeIcon icon={faCaretDown} />
                      </div>
                    }
                  />
                )}
              </div>
            )}
            {!isCourtPortalParcel && (
              <div className={classNames(styles.editor, "test")}>
                <RichEditor
                  register={handleRegisterEditor}
                  setContent={setContent}
                  ref={editorRef}
                  content={content}
                  allowShowCode={true}
                />

                {(isLoadingEmailContent || isLoadingContent) && (
                  <Spinner></Spinner>
                )}
              </div>
            )}
            <div
              className={classNames(
                styles.attachments,
                isCourtPortalParcel ? styles.large : null,
              )}
            >
              {!files.length && isCourtPortalParcel && (
                <FileUploader
                  multiple={true}
                  handleChange={async (f: any) => {
                    const newFiles = Array.from(f).map((f) => f) as File[];
                    setFiles([...files, newFiles].flat());
                  }}
                  classes={styles.drop}
                  dropMessageStyle={{
                    opacity: 0,
                  }}
                  name="file"
                  children={
                    <div className={styles.emptyAttachments}>
                      {/* <NoFileIcon className={styles.noFiles} /> */}
                      <DossierAnimation className={styles.noFiles} />
                      <div className={styles.emptyButtons}>
                        {t("common.dragAndDrop")}
                        <div className={styles.buttonCTA}>{t("common.chooseFromDisk")}</div>
                      </div>
                    </div>
                  }
                />
              )}

              <div className={styles.attachWrap}>
                <div className={styles.attachItems}>
                  {files.map((file, i) => (
                    <CreateOutgoingParcelFile
                      key={`files_${i}`}
                      index={i}
                      actualFile={i}
                      actualProgress={file.progress}
                      file={file}
                      onClick={() => handleShowPreview(file)}
                      handleRemove={removeFile}
                      hasError={!validateAttachment(file)}
                      errorMessage={getAttachmentErrorMessage(file)}
                      large={isCourtPortalParcel}
                      handleSign={handleSignFile}
                      courtPortalTypes={writingAttachmentTypes}
                      handleAttachmentTypeChanged={handleAttachmentTypeChanged}
                      initialAttachmentType={file.writingType}
                    />
                  ))}
                  {uploadedFiles.map((file, i) => (
                    <CreateOutgoingParcelFile
                      key={`uploadedFiles_${i}`}
                      index={i}
                      file={file}
                      onClick={() => handleShowPreview(file)}
                      handleRemove={removeUploadedFile}
                      handleSign={handleSignUploadedFile}
                      large={isCourtPortalParcel}
                      courtPortalTypes={writingAttachmentTypes}
                      handleAttachmentTypeChanged={handleAttachmentTypeChanged}
                    />
                  ))}
                </div>

                {/* <FileUploader
                  multiple={true}
                  handleChange={async (f: any) => {
                    // handleChooseFile(f);
                    for (const file of f) {
                      addNewFile(file);
                    }
                  }}
                  classes={styles.drop}
                  name="file"
                /> */}
              </div>
            </div>
            <div className={styles.footer}>
              <div className={styles.cost}>{t("packages.parcelCost", { cost: "0,00 zł" })}</div>

              <Tooltip title={getValidationErrors()}>
                <div>
                  <Button
                    loading={
                      isLoading || isLoadingEmailContent || isLoadingContent
                    }
                    className={styles.btn2}
                    disabled={isDisabled()}
                    onClick={handleSend}
                    // text="Wyślij"
                    element={<>{t("common.send")}</>}
                  />
                </div>
              </Tooltip>
            </div>
          </div>

          <div
            className={styles.dragOver}
            style={{ display: isDragOver ? "flex" : "none" }}
          >
            <div className={styles.inner}>
              <div>{t("common.dropFilesHere")}</div>
              <DossierAnimation className={styles.img} />
            </div>
          </div>

          {errorMesssage && (
            <div className={styles.error}>
              <div className={styles.icons}>
                <CloseIcon
                  className={styles.closeIcon}
                  onClick={handleCloseError}
                />
              </div>
              <div className={styles.inner}>
                <div className={styles.header}>{errorMesssage.title}</div>
                <div className={styles.message}>{errorMesssage.message}</div>
                <div className={styles.hint}>{errorMesssage.hint}</div>
              </div>
            </div>
          )}

          {isSignLoading && (
            <>
              <div className={styles.busyBg}></div>
              <div className={styles.busy}>
                <Spinner></Spinner>
                <div className={styles.busyText}>{t("certificate.signingInProgress")}</div>
              </div>
            </>
          )}
        </div>
        <input
          type="file"
          id="file"
          ref={inputFile}
          multiple={true}
          style={{ display: "none" }}
          onChange={handleChooseFile}
        />
        {showScan && (
          <ScanDocumentModal
            handleClose={() => setShowScan(false)}
            onComplete={(pdf) => addNewFile(pdf)}
          />
        )}
        {showDossier && (
          <CaseDossierWrapper
            handleAddFiles={handleAddFiles}
            c={c!}
            toggle={() => setShowDossier(false)}
          />
        )}
        {showFullDossier && (
          <CaseDossierWrapper
            handleAddFiles={handleAddFiles}
            c={null}
            toggle={() => setShowFullDossier(false)}
          />
        )}
        {blob && <ShowPreviewModal toggle={removeBlob} blob={blob} />}
        {showCase && (
          <CreateCaseModal
            isAnimation={false}
            onCreate={(c) => setCase(c)}
            onClose={() => setShowCase(false)}
          />
        )}

        {showCertificates && (
          <>
            <div className={styles.certificatesBg}></div>
            <div className={styles.certificatesWrapper}>
              <div className={styles.certificates}>
                <div className={styles.tit}>
                  {t("signature.chooseCertificate")}
                </div>
                <div className={styles.list}>
                  {certs.map((cert: ICertificate) => (
                    <div
                      onClick={() => handleSign(cert)}
                      className={styles.certbox}
                      key={cert.SerialNumber}
                    >
                      <div className={styles.certtitle}>
                        <div className={styles.name}>{cert.Name}</div>
                        <div className={styles.date}>
                          {moment(cert.ValidTo).format("DD.MM.YYYY").toString()}
                        </div>
                      </div>
                      <div className={styles.cert}>{cert.IssuerBy}</div>
                    </div>
                  ))}
                </div>
                <div className={styles.buttons}>
                  <HollowButton
                    onClick={() => setShowCertificates(false)}
                    text={t("common.cancel")}
                  ></HollowButton>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </ModalLayout>
  );
};

export default CreateOutgoingParcelModal;
